import type { DocSearchResult, DocStarredItem } from "@/lib/api";
import { fileIcon, fmtDateTime, fmtSize, PathTrail, rowKey } from "./shared";

/** Search results and the Recent / Starred / My-uploads views share this row. */
export type ListedItem = DocSearchResult | DocStarredItem;

/**
 * A flat, path-annotated listing — the shape every cross-folder view needs.
 * Unlike the folder table these rows show *where* each item lives, because the
 * whole point is that the result came from somewhere you weren't looking.
 *
 * Selection behaves exactly as it does in the folder table: click selects,
 * double-click opens.
 */
export function ResultsList({
  items,
  selected,
  cursor,
  onSelect,
  onOpen,
  onNavigate,
  onContextMenu,
}: {
  items: ListedItem[];
  selected: Set<string>;
  /** Index of the keyboard-focused row, or -1. */
  cursor: number;
  onSelect: (index: number, mods: { shift?: boolean; meta?: boolean }) => void;
  onOpen: (item: ListedItem) => void;
  onNavigate: (folderId: string | null) => void;
  onContextMenu: (e: React.MouseEvent, item: ListedItem) => void;
}) {
  return (
    <ul>
      {items.map((item, i) => {
        const isFolder = item.kind === "folder";
        const key = rowKey(item.kind, item.id);
        const { icon, color } = isFolder
          ? { icon: "fa-folder", color: "var(--color-warning)" }
          : fileIcon(item.mime, item.name);
        const excerpt = "excerpt" in item ? item.excerpt : null;
        return (
          <li
            key={key}
            data-row-index={i}
            onClick={(e) => onSelect(i, { shift: e.shiftKey, meta: e.metaKey || e.ctrlKey })}
            onDoubleClick={() => onOpen(item)}
            onContextMenu={(e) => onContextMenu(e, item)}
            className={`group flex cursor-pointer select-none items-start gap-3 border-b border-border-light px-4 py-3 transition-colors ${
              cursor === i
                ? "bg-[var(--color-row-selected)] ring-1 ring-inset ring-accent/40"
                : selected.has(key)
                  ? "bg-[var(--color-row-selected)]"
                  : `${i % 2 ? "bg-bg-secondary" : "bg-bg-card"} hover:bg-[var(--color-row-hover)]`
            }`}
          >
            <i className={`fa-solid ${icon} mt-0.5 shrink-0`} style={{ color }} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm text-text-heading">{item.name}</span>
                {!isFolder && item.isProtected && (
                  <i
                    className="fa-solid fa-lock text-[10px] text-warning"
                    title="Password protected"
                  />
                )}
                {item.starred && (
                  <i className="fa-solid fa-star text-[10px] text-warning" title="Starred" />
                )}
                {"matchedOn" in item && item.matchedOn === "content" && (
                  <span
                    className="rounded bg-bg-elevated px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wide text-text-muted"
                    title="Matched inside the file, not in its name"
                  >
                    in text
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-2">
                <PathTrail path={item.path} onNavigate={onNavigate} />
              </div>
              {excerpt && (
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-text-secondary">
                  {excerpt}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-3 pt-0.5">
              <span className="hidden text-[0.7rem] text-text-muted sm:block">
                {isFolder ? `${item.itemCount} items` : fmtSize(item.size)}
              </span>
              <span className="hidden text-[0.7rem] text-text-muted md:block">
                {fmtDateTime(item.updatedAt)}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
